"use client"

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { alpha } from '@mui/material/styles'
import { ChevronLeft, ChevronRight, Add, CheckCircle, Error, Edit, AccessTime } from "@mui/icons-material"
import { appointmentStatusLabelKey, classifyBookingResults } from '../../../utils/appointmentWorkflow'
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { format, startOfWeek, addDays, addWeeks, subWeeks, endOfWeek, isSameDay, isBefore, startOfDay, isToday } from "date-fns"
import { fr } from "date-fns/locale"
import dayjs from "dayjs"
import "dayjs/locale/fr"
import "dayjs/locale/ar"
import "dayjs/locale/en"
import {
  useAppointmentsAvailableSlotsRetrieveQuery,
  useAppointmentsConsecutiveSlotsRetrieveQuery,
  useAppointmentsMultiConsecutiveSlotsCreateMutation,
  useTeamsTeamMembersSalonMembersRetrieveQuery,
  useAuthProfileRetrieveQuery,
  useAppointmentsCreateMutation,
} from "../../../services/GLOWClient"
import { useAuth } from "../../../context/AuthContext"
import AuthModal from "../../Auth/AuthModal"
import AppointmentBookingDialog from "./AppointmentBookingDialog"
import MuiDialog from "../../../SystemDesign/MuiDialog"
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext'
import moment from 'moment'
import 'moment/locale/fr'
import 'moment/locale/en-ca'
import 'moment/locale/ar'
import Button from '../../common/salonPublicAppearance/SalonPublicButton'
import IconButton from '../../common/salonPublicAppearance/SalonPublicIconButton'

const AppointmentCalendar = ({
  salonId,
  selectedService,
  onSelectSlot,
  onClose,
  onAddService,
  existingServices = [],
  salonDetails = null,
  rebookAppointmentData = null,
  allServices = null,
  enableServiceSelection = false,
  isUpdateMode = false,
}) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { tokens: theme } = useSalonPublicTheme()

  // Set moment locale based on current language
  useEffect(() => {
    const momentLocale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en' : 'fr'
    moment.locale(momentLocale)
  }, [i18n.language])

  // Set dayjs locale based on current language
  useEffect(() => {
    const localeMap = {
      'fr': 'fr',
      'ar': 'ar',
      'en': 'en'
    }
    dayjs.locale(localeMap[i18n.language] || 'en')
  }, [i18n.language])
  
  // Get current user profile for client information
  const { isAuthenticated } = useAuth()
  const { data: userProfile, refetch: refetchUserProfile, isUninitialized: isProfileUninitialized } = useAuthProfileRetrieveQuery(undefined, { skip: !isAuthenticated() })
  
  // Mobile responsive hooks
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'))

  // State for confirmation dialog - no longer used, keeping for compatibility
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [appointmentResults, setAppointmentResults] = useState([])
  const [bookingOutcome, setBookingOutcome] = useState('confirmed')
  const [errorMessage, setErrorMessage] = useState('')
  // const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [appointmentToConfirm, setAppointmentToConfirm] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  // State for authentication modal
  const [authModalOpen, setAuthModalOpen] = useState(false)
  // State for appointment booking dialog
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false)
  // State for form fields
  const [selectedTeamMember, setSelectedTeamMember] = useState("")
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const [selectedSlot, setSelectedSlot] = useState("")
  const [weeklySlots, setWeeklySlots] = useState({})

  // State for multi-service booking
  const [selectedServices, setSelectedServices] = useState(() => {
    // Initialize with existing services or create a new service entry
    if (existingServices.length > 0) {
      return [...existingServices] // Create a copy to avoid mutation
    } else if (selectedService) {
      return [
        {
          serviceId: selectedService.service_id,
          serviceName: selectedService.name,
          selectedSlot: null,
          selectedDate: null,
          selectedTime: null,
          teamMember: null,
        },
      ]
    }
    return []
  })

  const [currentServiceIndex, setCurrentServiceIndex] = useState(() => {
    // Set current index to the last service (newly added one)
    if (existingServices.length > 0) {
      return existingServices.length - 1
    }
    return 0
  })

  const [timePeriod, setTimePeriod] = useState("all") // "all", "morning", or "afternoon"
  const [isCreating, setIsCreating] = useState(false)
  const [mobileCurrentDate, setMobileCurrentDate] = useState(new Date()) // For mobile day navigation
  const [isConsecutive, setIsConsecutive] = useState(false) // Toggle for consecutive booking
  const [consecutiveDate, setConsecutiveDate] = useState(format(new Date(), "yyyy-MM-dd")) // Date for consecutive booking
  const [multiConsecutiveChains, setMultiConsecutiveChains] = useState([])
  const [multiConsecutiveDate, setMultiConsecutiveDate] = useState(null)
  // Conflit de sélection (plusieurs RDV même employé/même créneau ou chevauchement)
  const [selectionConflictOpen, setSelectionConflictOpen] = useState(false)
  const [selectionConflictInfo, setSelectionConflictInfo] = useState({ count: 0, teamMember: null, date: null })

  // Navigation functions for mobile day switching
  const handlePreviousDay = () => {
    const previousDay = addDays(mobileCurrentDate, -1)
    // Don't allow navigation to past dates
    if (!isBefore(previousDay, startOfDay(new Date()))) {
      setMobileCurrentDate(previousDay)
    }
  }

  const handleNextDay = () => {
    const nextDay = addDays(mobileCurrentDate, 1)
    setMobileCurrentDate(nextDay)
  }

  // Check if previous day navigation should be disabled
  const isPreviousDayDisabled = isBefore(addDays(mobileCurrentDate, -1), startOfDay(new Date()))

  // Sync currentWeek with mobileCurrentDate on mobile
  React.useEffect(() => {
    if (isMobile) {
      // Set currentWeek to the start of the week containing mobileCurrentDate
      const weekStart = startOfWeek(mobileCurrentDate, { weekStartsOn: 1 })
      setCurrentWeek(weekStart)
    }
  }, [mobileCurrentDate, isMobile])

  // Update selectedServices when existingServices prop changes
  React.useEffect(() => {

    // Always sync with existingServices to maintain consistency
    // But preserve any slot selections that were made
    if (existingServices.length > 0) {
      const mergedServices = existingServices.map((existingService, index) => {
        const currentService = selectedServices[index]
        // If current service has slot selections, preserve them
        if (currentService && currentService.selectedSlot) {
          return currentService
        }
        return existingService
      })

      setSelectedServices(mergedServices)

      const newIndex = existingServices.length - 1
      setCurrentServiceIndex(newIndex)
    }
  }, [existingServices])

  // Handle rebooking - prefill team member selection and set current week to appointment date
  React.useEffect(() => {
    if (rebookAppointmentData) {
      
      // Set team member
      if (rebookAppointmentData.team_member) {
        setSelectedTeamMember(rebookAppointmentData.team_member)
      }
      
      // Set current week to show the original appointment date
      if (rebookAppointmentData.start_time) {
        const appointmentDate = new Date(rebookAppointmentData.start_time)
        setCurrentWeek(appointmentDate)
      }
    }
  }, [rebookAppointmentData])

  // Get current service details
  const currentService = selectedServices[currentServiceIndex]
  const serviceId = currentService?.serviceId || selectedService?.service_id
  const serviceName = currentService?.serviceName || selectedService?.name



  // Get week dates
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }) // Monday start
  const allWeekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  // Filter to show only today and future days
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day for accurate comparison

  const weekDays = allWeekDays.filter((date) => {
    const dateOnly = new Date(date)
    dateOnly.setHours(0, 0, 0, 0)
    return dateOnly >= today
  })

  // Format dates for API calls
  const weekDatesFormatted = weekDays.map((date) => format(date, "yyyy-MM-dd"))

  // Fetch team members for this salon
  const {
    data: teamMembers,
    isLoading: teamMembersLoading,
    error: teamMembersError,
  } = useTeamsTeamMembersSalonMembersRetrieveQuery({ salon: salonId }, { skip: !salonId })

  // Filter team members by service capability
  const filteredTeamMembers = React.useMemo(() => {
    if (!teamMembers) return []
    
    // If no service is selected, show all team members
    if (!serviceId) return teamMembers
    
    // Filter team members who can provide the selected service
    const filtered = teamMembers.filter(member => {
      // Check if the team member has the service in their service_ids array
      const hasService = member.service_ids && member.service_ids.includes(parseInt(serviceId))
      
      // Debug logging
      console.log('Team member:', member.user_details?.first_name, member.user_details?.last_name)
      console.log('  - service_ids:', member.service_ids)
      console.log('  - Looking for serviceId:', serviceId, '(parsed:', parseInt(serviceId), ')')
      console.log('  - Has service:', hasService)
      
      return hasService
    })
    
    console.log('Filtered team members count:', filtered.length, 'out of', teamMembers.length)
    return filtered
  }, [teamMembers, serviceId])

  // Fetch available slots for each day of the week - call hooks at top level
  // We need to call hooks for all 7 days but skip the ones we don't need
  const slotsQuery0 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[0] ? format(allWeekDays[0], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[0] ||
        (allWeekDays[0] && new Date(allWeekDays[0]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  const slotsQuery1 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[1] ? format(allWeekDays[1], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[1] ||
        (allWeekDays[1] && new Date(allWeekDays[1]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  const slotsQuery2 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[2] ? format(allWeekDays[2], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[2] ||
        (allWeekDays[2] && new Date(allWeekDays[2]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  const slotsQuery3 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[3] ? format(allWeekDays[3], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[3] ||
        (allWeekDays[3] && new Date(allWeekDays[3]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  const slotsQuery4 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[4] ? format(allWeekDays[4], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[4] ||
        (allWeekDays[4] && new Date(allWeekDays[4]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  const slotsQuery5 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[5] ? format(allWeekDays[5], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[5] ||
        (allWeekDays[5] && new Date(allWeekDays[5]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  const slotsQuery6 = useAppointmentsAvailableSlotsRetrieveQuery(
    {
      serviceId: serviceId,
      teamMemberId: selectedTeamMember || undefined,
      date: allWeekDays[6] ? format(allWeekDays[6], "yyyy-MM-dd") : "",
    },
    {
      skip:
        !serviceId ||
        !allWeekDays[6] ||
        (allWeekDays[6] && new Date(allWeekDays[6]).setHours(0, 0, 0, 0) < today.getTime()),
      refetchOnMountOrArgChange: true,
    },
  )

  // Combine all queries into an array for easier handling
  const slotsQueries = [slotsQuery0, slotsQuery1, slotsQuery2, slotsQuery3, slotsQuery4, slotsQuery5, slotsQuery6]

  // Effective team members for consecutive booking: use per-service team member if set,
  // otherwise fallback to the first service's team member or the global selectedTeamMember.
  // If none of the preferred IDs are compatible, pick a random compatible team member.
  const resolveEffectiveTeamMember = (preferredIds, serviceId) => {
    if (!serviceId || !teamMembers) return null

    const tryPreferred = (candidateId) => {
      if (!candidateId) return null
      const member = teamMembers.find((m) => m.team_member_id === Number(candidateId))
      if (!member || !Array.isArray(member.service_ids)) return null
      return member.service_ids.includes(Number(serviceId)) ? candidateId : null
    }

    // 1) Try preferred IDs in order
    for (const pid of preferredIds) {
      const valid = tryPreferred(pid)
      if (valid) return valid
    }

    // 2) No valid preferred -> pick a random compatible team member
    const eligible = teamMembers.filter(
      (m) => Array.isArray(m.service_ids) && m.service_ids.includes(Number(serviceId)),
    )
    if (!eligible.length) return null

    const randomIndex = Math.floor(Math.random() * eligible.length)
    const randomMember = eligible[randomIndex]
    return randomMember ? randomMember.team_member_id : null
  }

  const effectiveTeamMember1 = resolveEffectiveTeamMember(
    [selectedServices[0]?.teamMember, selectedTeamMember],
    selectedServices[0]?.serviceId,
  )

  const effectiveTeamMember2 = resolveEffectiveTeamMember(
    [selectedServices[1]?.teamMember, selectedServices[0]?.teamMember, selectedTeamMember],
    selectedServices[1]?.serviceId,
  )

  // Consecutive slots query - when we have 2 services and effective team members resolved
  const canFetchConsecutiveSlots = 
    selectedServices.length === 2 && 
    selectedServices[0]?.serviceId && 
    selectedServices[1]?.serviceId &&
    effectiveTeamMember1 &&
    effectiveTeamMember2 &&
    Boolean(consecutiveDate)

  // Consecutive slots API query
  const {
    data: consecutiveSlotsData,
    isLoading: consecutiveSlotsLoading,
    error: consecutiveSlotsError,
  } = useAppointmentsConsecutiveSlotsRetrieveQuery(
    {
      service1Id: Number(selectedServices[0]?.serviceId),
      teamMember1Id: Number(effectiveTeamMember1),
      service2Id: Number(selectedServices[1]?.serviceId),
      teamMember2Id: Number(effectiveTeamMember2),
      date: consecutiveDate,
      bufferMinutes: 0,
    },
    {
      skip: !canFetchConsecutiveSlots,
    }
  )

  // Function to refetch all slots queries
  const refetchAllSlots = () => {
    slotsQueries.forEach((query, index) => {
      // Only refetch if the query is not skipped, has valid data, and is not in error state
      if (query.refetch && !query.isUninitialized && !query.isError && serviceId && selectedTeamMember) {
        try {
          query.refetch()
        } catch (error) {
          console.warn(`Failed to refetch slots query ${index}:`, error)
        }
      }
    })
  }

  // Combine loading states - only check queries for days we're actually showing
  const relevantQueries = slotsQueries.filter((_, index) => {
    const day = allWeekDays[index]
    if (!day) return false
    const dayOnly = new Date(day)
    dayOnly.setHours(0, 0, 0, 0)
    return dayOnly >= today
  })

  const slotsLoading = relevantQueries.some((query) => query.isLoading)
  const slotsError = relevantQueries.find((query) => query.error)?.error

  // Update weekly slots when queries change
  useEffect(() => {
    const newWeeklySlots = {}
    allWeekDays.forEach((date, index) => {
      const dateStr = format(date, "yyyy-MM-dd")
      const dayOnly = new Date(date)
      dayOnly.setHours(0, 0, 0, 0)

      // Only include data for present and future days
      if (dayOnly >= today) {
        const queryResult = slotsQueries[index]
        if (queryResult?.data) {
          newWeeklySlots[dateStr] = queryResult.data
        }
      }
    })
    setWeeklySlots(newWeeklySlots)
    setSelectedSlot("")
  }, [
    slotsQuery0.data,
    slotsQuery1.data,
    slotsQuery2.data,
    slotsQuery3.data,
    slotsQuery4.data,
    slotsQuery5.data,
    slotsQuery6.data,
    allWeekDays.map((d) => format(d, "yyyy-MM-dd")).join(","),
  ])

  // Initialize selected services when component mounts - only if no existing services
  useEffect(() => {
    if (serviceId && serviceName && selectedServices.length === 0 && existingServices.length === 0) {
      setSelectedServices([
        {
          serviceId,
          serviceName,
          selectedSlot: null,
          selectedDate: null,
          selectedTime: null,
          teamMember: null,
        },
      ])
    }
    setSelectedTeamMember("")
  }, [serviceId, serviceName])

  // Handle team member selection
  const handleTeamMemberChange = (event) => {
    setSelectedTeamMember(event.target.value)
  }

  // Handle week navigation
  const goToPreviousWeek = () => {
    const prevWeek = subWeeks(currentWeek, 1)
    // Don't allow navigation to weeks that would show only past days
    const prevWeekStart = startOfWeek(prevWeek, { weekStartsOn: 1 })
    const prevWeekEnd = endOfWeek(prevWeek, { weekStartsOn: 1 })

    // Check if any day in the previous week is today or later
    if (prevWeekEnd >= today) {
      setCurrentWeek(prevWeek)
    }
  }

  const goToNextWeek = () => {
    setCurrentWeek((prev) => addWeeks(prev, 1))
  }

  // Check if we can go to previous week (has at least one present/future day)
  const canGoToPreviousWeek = () => {
    const prevWeek = subWeeks(currentWeek, 1)
    const prevWeekEnd = endOfWeek(prevWeek, { weekStartsOn: 1 })
    const todayEnd = new Date(today)
    todayEnd.setHours(23, 59, 59, 999)
    return prevWeekEnd >= todayEnd
  }

  // Mutation for creating appointment
  const [createAppointment, { isLoading: isMutationLoading, error: createError }] = useAppointmentsCreateMutation()
  const [createMultiConsecutiveSlots, { isLoading: isMultiConsecutiveLoading }] =
    useAppointmentsMultiConsecutiveSlotsCreateMutation()

  // State to prevent double-click issues
  const [isProcessingClick, setIsProcessingClick] = useState(false)

  // Handle slot selection
  const handleSlotClick = (slot, date, teamMemberId = null) => {
    // Prevent double-click
    if (isProcessingClick) {
      console.log('Click ignored - already processing')
      return
    }

    // Check if user is authenticated before allowing slot selection
    if (!isAuthenticated()) {
      setAuthModalOpen(true)
      return
    }

    // Set processing flag and reset after a short delay
    setIsProcessingClick(true)
    setTimeout(() => setIsProcessingClick(false), 300)

    setSelectedSlot(`${date}-${slot}`)

    const daySlots = weeklySlots[date]

    // If no team member is explicitly selected but we're showing all team members' slots
    // we need to determine which team member has this slot available
    let effectiveTeamMember = selectedTeamMember

    if (!selectedTeamMember && daySlots?.all_team_members_slots) {
      if (teamMemberId) {
        // If a team member ID was provided with the slot, use that
        effectiveTeamMember = teamMemberId
      } else {
        // Find the first team member who has this slot available
        for (const [tmId, tmData] of Object.entries(daySlots.all_team_members_slots)) {
          if (tmData.available_slots.includes(slot)) {
            effectiveTeamMember = Number.parseInt(tmId)
            break
          }
        }
      }
    }

    const currentSvcId = selectedServices[currentServiceIndex]?.serviceId || selectedService?.service_id
    if (effectiveTeamMember && currentSvcId) {
      const hasOverlap = isOverlapWithExistingSelection(date, slot, effectiveTeamMember, currentSvcId)
      if (hasOverlap) {
        setSelectionConflictOpen(true)
        return
      }
    }

    // Update the current service with selected slot information
    setSelectedServices((prev) => {
      const updated = [...prev]
      if (updated[currentServiceIndex]) {
        updated[currentServiceIndex] = {
          ...updated[currentServiceIndex],
          selectedSlot: `${date}-${slot}`,
          selectedDate: date,
          selectedTime: slot,
          teamMember: effectiveTeamMember || null,
        }
      }
      return updated
    })

    // In update mode, don't immediately call onSelectSlot - wait for confirmation button
  }

  // Handle adding another service
  const handleAddService = () => {
    if (onAddService) {
      // Pass current selected services to parent component
      onAddService(selectedServices)
    }
  }

  const fetchMultiConsecutiveSlotsForDate = async (dateStrOverride) => {
    try {
      // Déterminer un professionnel de référence si certains services n'en ont pas
      const primaryTeamMember =
        selectedTeamMember ||
        (selectedServices.find((s) => s.teamMember)?.teamMember ?? null)

      const servicesPayload = selectedServices
        .filter((s) => s.serviceId)
        .map((s) => {
          const resolvedTeamMemberId = resolveEffectiveTeamMember(
            [s.teamMember, primaryTeamMember, selectedTeamMember],
            s.serviceId,
          )
          return {
            service_id: Number(s.serviceId),
            team_member_id: resolvedTeamMemberId ? Number(resolvedTeamMemberId) : null,
          }
        })
        .filter((s) => s.team_member_id !== null)

      if (servicesPayload.length < 2) {
        setSnackbarMessage(
          "Sélectionnez au moins deux services ou un professionnel compatible pour les créneaux consécutifs.",
        )
        setSnackbarOpen(true)
        return
      }

      const firstDateWithSelection = selectedServices
        .map((s) => s.selectedDate)
        .filter(Boolean)
        .sort()[0]

      const dateToUse =
        dateStrOverride || firstDateWithSelection || format(new Date(), "yyyy-MM-dd")

      const result = await createMultiConsecutiveSlots({
        body: {
          date: dateToUse,
          buffer_minutes: 0,
          slot_interval_minutes: 5,
          allow_next_slot: true,
          max_gap_minutes: 30,
          services: servicesPayload,
        },
      }).unwrap()

      const chains = result && result.chains ? result.chains : []
      if (!chains.length) {
        setSnackbarMessage("Aucun créneau consécutif trouvé pour cette combinaison de services.")
        setSnackbarOpen(true)
        setMultiConsecutiveChains([])
        return
      }

      const chainDate = result.date || dateToUse
      setMultiConsecutiveChains(chains)
      setMultiConsecutiveDate(chainDate)
    } catch (error) {
      console.error("Failed to get multi consecutive slots", error)
      setSnackbarMessage(
        getApiErrorMessage(error, "Erreur lors de la recherche de créneaux consécutifs multiples."),
      )
      setSnackbarOpen(true)
    }
  }

  // Handle main "Consécutif?" button
  const handleConsecutiveButtonClick = async () => {
    // Case 1: exactly 2 services -> enable UI consecutive mode and let the 2-service API run
    if (selectedServices.length === 2) {
      // When enabling consecutive mode, choose a relevant date (first selectedDate if any, otherwise today)
      if (!isConsecutive) {
        const firstDateWithSelection = selectedServices
          .map((s) => s.selectedDate)
          .filter(Boolean)
          .sort()[0]

        const dateToUse = firstDateWithSelection || format(new Date(), "yyyy-MM-dd")
        setConsecutiveDate(dateToUse)
      }

      setIsConsecutive((prev) => !prev)
      return
    }

    // Case 2: more than 2 services -> toggle consecutive mode and use multi_consecutive_slots
    if (selectedServices.length > 2) {
      // Si on est déjà en mode consécutif, on le désactive
      if (isConsecutive) {
        setIsConsecutive(false)
        return
      }

      // Sinon on active le mode consécutif et on charge les créneaux multi-services pour la date choisie
      await fetchMultiConsecutiveSlotsForDate()
      setIsConsecutive(true)
    }
  }

  const getApiErrorMessage = (error, defaultMessage) => {
    if (!error) return defaultMessage
    const data = error.data || error.error || error.message
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
      if (Array.isArray(data.start_time) && data.start_time.length > 0) {
        return data.start_time.join("\n")
      }
      if (Array.isArray(data.details) && data.details.length > 0) {
        return data.details.join("\n")
      }
      if (typeof data.details === "string") return data.details
      if (typeof data.error === "string") return data.error
    }
    return defaultMessage
  }

  const filterConsecutiveSlotsForDate = (slots, dateStr) => {
    if (!Array.isArray(slots)) return []
    if (!dateStr) return slots

    const todayStr = format(new Date(), "yyyy-MM-dd")
    if (dateStr !== todayStr) {
      return slots
    }

    const now = new Date()
    return slots.filter((slot) => {
      if (!slot.start_time) return true
      const [hours, minutes] = String(slot.start_time).split(":").map(Number)
      const slotDateTime = new Date()
      slotDateTime.setHours(hours || 0, minutes || 0, 0, 0)
      return slotDateTime >= now
    })
  }

  // Handle consecutive slot selection
  const handleConsecutiveSlotSelect = (slot) => {
    // Update both services with the consecutive slot information
    setSelectedServices((prev) => {
      const updated = [...prev]
      if (updated.length >= 2) {
        // First service
        updated[0] = {
          ...updated[0],
          selectedSlot: `${consecutiveDate}-${slot.service1_start}`,
          selectedDate: consecutiveDate,
          selectedTime: slot.service1_start,
          selectedEndTime: slot.service1_end,
        }
        // Second service
        updated[1] = {
          ...updated[1],
          selectedSlot: `${consecutiveDate}-${slot.service2_start}`,
          selectedDate: consecutiveDate,
          selectedTime: slot.service2_start,
          selectedEndTime: slot.service2_end,
        }
      }
      return updated
    })
  }

  const handleMultiConsecutiveChainSelect = (chain) => {
    if (!multiConsecutiveDate) return
    setSelectedServices((prev) =>
      prev.map((svc, idx) => {
        const chainSvc = chain.services && chain.services[idx]
        if (!chainSvc) return svc
        return {
          ...svc,
          selectedSlot: `${multiConsecutiveDate}-${chainSvc.service_start}`,
          selectedDate: multiConsecutiveDate,
          selectedTime: chainSvc.service_start,
          selectedEndTime: chainSvc.service_end,
          teamMember: svc.teamMember || chainSvc.team_member_id,
        }
      })
    )
  }

  const visibleConsecutiveSlots = consecutiveSlotsData
    ? filterConsecutiveSlotsForDate(consecutiveSlotsData.consecutive_slots, consecutiveDate)
    : []

  const visibleMultiConsecutiveChains = multiConsecutiveChains.length > 0 && multiConsecutiveDate
    ? filterConsecutiveSlotsForDate(multiConsecutiveChains, multiConsecutiveDate)
    : multiConsecutiveChains



  // Handle updating appointment (for update mode)
  const handleUpdateAppointment = () => {
    if (!selectedServices.length || !selectedServices[0].selectedSlot) return;
    const currentServiceData = selectedServices[0];
    if (onSelectSlot) {
      onSelectSlot({
        selectedDate: currentServiceData.selectedDate,
        selectedTime: currentServiceData.selectedTime,
        teamMember: currentServiceData.teamMember,
        serviceId: currentServiceData.serviceId,
      });
    }
  };

  // --- UI helper: Dialog de conflit de sélection ---
  const renderSelectionConflictDialog = () => (
    <MuiDialog
      open={selectionConflictOpen}
      onExternalClose={() => setSelectionConflictOpen(false)}
      title="⚠️ Conflit de sélection"
      subtitle="Rendez-vous en conflit"
      content={
        <Box>
          <Typography sx={{ mb: 2, fontSize: '1rem' }}>
            Vous avez pris des rendez-vous avec <strong>le même professionnel</strong> sur des créneaux qui se chevauchent.
          </Typography>
          <Typography sx={{ color: theme.colors.text.secondary, mb: 3 }}>
            Souhaitez-vous choisir des <strong>créneaux consécutifs</strong> pour ces services ?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={() => {
                setSelectionConflictOpen(false)
                // Réinitialiser les sélections pour forcer l'utilisateur à choisir d'autres créneaux
                setSelectedServices(prev => prev.map(s => ({
                  ...s,
                  selectedSlot: null,
                  selectedDate: null,
                  selectedTime: null
                })))
              }}
              sx={{ minWidth: '150px' }}
            >
              Choisir d'autres créneaux
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (selectedServices.length === 2) {
                  setIsConsecutive(true)
                  setConsecutiveDate(format(new Date(), "yyyy-MM-dd"))
                  setSelectionConflictOpen(false)
                  return
                }

                // Plus de deux services : activer le mode consécutif multi-services
                try {
                  const firstDateWithSelection = selectedServices
                    .map((s) => s.selectedDate)
                    .filter(Boolean)
                    .sort()[0]

                  const dateToUse = firstDateWithSelection || format(new Date(), "yyyy-MM-dd")

                  // Mettre à jour la date utilisée pour les chaînes multi-services
                  setMultiConsecutiveDate(dateToUse)

                  // Charger les créneaux multi-services pour cette date
                  await fetchMultiConsecutiveSlotsForDate(dateToUse)

                  // Activer le mode consécutif pour masquer l'ancien calendrier
                  setIsConsecutive(true)

                  setSelectionConflictOpen(false)
                } catch (error) {
                  console.error("Failed to get multi consecutive slots from conflict dialog", error)
                  setSnackbarMessage(
                    getApiErrorMessage(error, "Erreur lors de la recherche de créneaux consécutifs multiples."),
                  )
                  setSnackbarOpen(true)
                  setSelectionConflictOpen(false)
                }
              }}
              sx={{ 
                minWidth: '150px',
                backgroundColor: theme.colors.primary[500],
                '&:hover': {
                  backgroundColor: theme.colors.primary[600]
                }
              }}
            >
              Créneaux consécutifs
            </Button>
          </Box>
        </Box>
      }
      maxWidth="sm"
    />
  )

  // Handle confirming all appointments
  const handleConfirmAllAppointments = async () => {
    // Prevent double submission
    if (isCreating) return

    if (!userProfile) {
      setErrorMessage("Impossible de créer les rendez-vous: informations utilisateur manquantes")
      setErrorDialogOpen(true)
      return
    }

    // Simple pre-check: detect overlap for same team member on same date
    try {
      const items = selectedServices
        .filter(s => s.selectedDate && s.selectedTime && s.teamMember)
        .map(s => {
          // Resolve duration
          let serviceDuration = 60
          const daySlots = weeklySlots?.[s.selectedDate]
          if (daySlots?.debug_info?.service_duration) {
            serviceDuration = daySlots.debug_info.service_duration
          } else if (Array.isArray(allServices) && allServices.length > 0) {
            const svc = allServices.find(x => (x.service_id || x.id) === s.serviceId)
            if (svc?.duration) serviceDuration = svc.duration
          } else if (selectedService?.duration && (s.serviceId === selectedService?.service_id || s.serviceId === selectedService?.id)) {
            serviceDuration = selectedService.duration
          }
          const start = new Date(`${s.selectedDate}T${s.selectedTime}:00`)
          const end = s.selectedEndTime
            ? new Date(`${s.selectedDate}T${s.selectedEndTime}:00`)
            : new Date(start.getTime() + serviceDuration * 60000)
          return { ...s, start, end }
        })

      let conflict = false
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i], b = items[j]
          if (a.teamMember === b.teamMember && a.selectedDate === b.selectedDate) {
            const overlap = a.start < b.end && a.end > b.start
            if (overlap) { conflict = true; break }
          }
        }
        if (conflict) break
      }
      if (conflict) {
        setSelectionConflictOpen(true)
        return
      }
    } catch (e) {
      console.warn('Pre-check failed', e)
    }

    setIsCreating(true)
    try {
      const appointmentPromises = selectedServices.map(async (service) => {
        // Resolve duration
        let serviceDuration = 60
        const daySlots = weeklySlots?.[service.selectedDate]
        if (daySlots?.debug_info?.service_duration) {
          serviceDuration = daySlots.debug_info.service_duration
        } else if (Array.isArray(allServices) && allServices.length > 0) {
          const svc = allServices.find(s => (s.service_id || s.id) === service.serviceId)
          if (svc?.duration) serviceDuration = svc.duration
        } else if (selectedService?.duration && (service.serviceId === selectedService?.service_id || service.serviceId === selectedService?.id)) {
          serviceDuration = selectedService.duration
        }

        const start = new Date(`${service.selectedDate}T${service.selectedTime}:00`)
        const end = new Date(start.getTime() + serviceDuration * 60000)

        const formatLocalDateTime = (date) => {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, "0")
          const day = String(date.getDate()).padStart(2, "0")
          const hours = String(date.getHours()).padStart(2, "0")
          const minutes = String(date.getMinutes()).padStart(2, "0")
          const seconds = String(date.getSeconds()).padStart(2, "0")
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }

        const start_time = formatLocalDateTime(start)
        const end_time = formatLocalDateTime(end)

        const teamMemberId = service.teamMember ? Number(service.teamMember) : null

        const payload = {
          salon: salonId,
          service: Number(service.serviceId),
          team_member: teamMemberId,
          client: Number(userProfile.id),
          start_time,
          end_time,
        }

        const result = await createAppointment({ appointmentCreateRequest: payload }).unwrap()
        return { ...result, serviceName: service.serviceName }
      })

      const results = await Promise.all(appointmentPromises)
      setAppointmentResults(results)
      setBookingOutcome(classifyBookingResults(results))
      setSuccessDialogOpen(true)

      // Reset
      setSelectedServices([])
      setSelectedSlot("")
      setCurrentServiceIndex(0)
      setIsConsecutive(false)
    } catch (error) {
      console.error('Failed to create appointments:', error)
      const errorMsg = getApiErrorMessage(error, "Erreur lors de la création des rendez-vous")
      setErrorMessage(errorMsg)
      setErrorDialogOpen(true)
    } finally {
      setIsCreating(false)
    }
  }

  // Handle appointment confirmation - no longer used since we use AppointmentBookingDialog
  // const handleConfirmAppointment = async () => {
  //   if (!appointmentToConfirm || !userProfile) {
  //     setSnackbarMessage('Impossible de créer le rendez-vous: informations manquantes');
  //     setSnackbarOpen(true);
  //     return;
  //   }

  //   try {
  //     // Get service duration from availableSlots to calculate end_time
  //     const serviceDuration = availableSlots?.debug_info?.service_duration || 60; // Default to 60 minutes if not available

  //     // Combine date and time to create start_time
  //     const dateStr = appointmentToConfirm.date; // Format: YYYY-MM-DD
  //     const timeStr = appointmentToConfirm.time; // Format: HH:MM
  //     const startDateTime = new Date(`${dateStr}T${timeStr}:00`);

  //     // Calculate end_time by adding service duration
  //     const endDateTime = new Date(startDateTime.getTime() + serviceDuration * 60000);

  //     // Format dates correctly without timezone conversion
  //     // Use local time format to avoid timezone issues
  //     const formatLocalDateTime = (date) => {
  //       const year = date.getFullYear();
  //       const month = String(date.getMonth() + 1).padStart(2, '0');
  //       const day = String(date.getDate()).padStart(2, '0');
  //       const hours = String(date.getHours()).padStart(2, '0');
  //       const minutes = String(date.getMinutes()).padStart(2, '0');
  //       const seconds = String(date.getSeconds()).padStart(2, '0');
  //       return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  //     };

  //     const start_time = formatLocalDateTime(startDateTime);
  //     const end_time = formatLocalDateTime(endDateTime);

  //     const appointmentData = {
  //       salon: salonId,
  //       service: appointmentToConfirm.service,
  //       team_member: appointmentToConfirm.teamMember,
  //       client: userProfile.id,
  //       start_time: start_time,
  //       end_time: end_time,
  //       status: 'pending'
  //     };

  //     const result = await createAppointment({appointmentCreateRequest: appointmentData}).unwrap();
  //     setConfirmDialogOpen(false);
  //     setSnackbarMessage('Rendez-vous créé avec succès!');
  //     setSnackbarOpen(true);

  //     // Reset selection
  //     setSelectedSlot('');

  //     if (onSelectSlot) {
  //       onSelectSlot(appointmentToConfirm);
  //     }
  //   } catch (error) {
  //     console.error('Failed to create appointment:', error);
  //     setSnackbarMessage('Erreur lors de la création du rendez-vous');
  //     setSnackbarOpen(true);
  //   }
  // };

  // Handle dialog close - no longer used since we use AppointmentBookingDialog
  // const handleDialogClose = () => {
  //   setConfirmDialogOpen(false);
  // };

  // Handle auth modal close
  const handleAuthModalClose = () => {
    setAuthModalOpen(false)
  }

  // Handle successful authentication
  const handleAuthSuccess = (userData) => {
    setAuthModalOpen(false)
    
    // Refetch user profile to get updated authentication data (only if query was already started)
    if (!isProfileUninitialized && refetchUserProfile) {
      refetchUserProfile()
    }
    
    // Refetch available slots to ensure they're loaded with authenticated user
    refetchAllSlots()
    
    // Show a message to user that they can now select a slot
    setSnackbarMessage("Authentification réussie ! Vous pouvez maintenant sélectionner un créneau.")
    setSnackbarOpen(true)
  }

  // Handle booking dialog close
  const handleBookingDialogClose = () => {
    setBookingDialogOpen(false)
    setAppointmentToConfirm(null)
  }

  // Handle successful appointment booking
  const handleAppointmentSuccess = (appointmentData) => {
    setBookingDialogOpen(false)
    setAppointmentToConfirm(null)
    setSnackbarMessage("Rendez-vous créé avec succès!")
    setSnackbarOpen(true)

    // Reset selection
    setSelectedSlot("")

    // Refetch available slots for all days to update the calendar
    // Force refetch by updating the current week state
    setCurrentWeek(new Date(currentWeek))

    if (onSelectSlot) {
      // In update mode, pass the slot selection data directly
      if (isUpdateMode && selectedServices.length > 0) {
        const currentServiceData = selectedServices[currentServiceIndex]
        if (currentServiceData && currentServiceData.selectedDate && currentServiceData.selectedTime) {
          onSelectSlot({
            selectedDate: currentServiceData.selectedDate,
            selectedTime: currentServiceData.selectedTime,
            teamMember: currentServiceData.teamMember,
            serviceId: currentServiceData.serviceId
          })
          return
        }
      }
      onSelectSlot(appointmentData)
    }
  }

  // // Debug snackbar state changes
  // useEffect(() => {
  //   console.log("Snackbar state changed:", { snackbarOpen, snackbarMessage })
  // }, [snackbarOpen, snackbarMessage])

  // Get available slots for a specific date, divided into morning and afternoon
  const getAvailableSlotsForDate = (date) => {
    const daySlots = weeklySlots[date]

    if (!daySlots) {
      return { morning: [], afternoon: [] }
    }

    const now = new Date()
    const today = format(now, "yyyy-MM-dd")
    const shouldFilterByTime = date === today

    let slots = []

    if (selectedTeamMember) {
      // Single team member case
      if (Array.isArray(daySlots.available_slots_iso) && daySlots.available_slots_iso.length > 0) {
        slots = daySlots.available_slots_iso.map(iso => moment.parseZone(iso).format('HH:mm'))
      } else {
        slots = [...(daySlots.available_slots || [])]
      }
    } else {
      // No team member selected - combine all slots from all team members
      if (daySlots.all_team_members_slots) {
        const allSlots = []
        Object.entries(daySlots.all_team_members_slots).forEach(([memberId, memberData]) => {
          if (Array.isArray(memberData.available_slots_iso) && memberData.available_slots_iso.length > 0) {
            allSlots.push(...memberData.available_slots_iso.map(iso => moment.parseZone(iso).format('HH:mm')))
          } else if (memberData.available_slots && Array.isArray(memberData.available_slots)) {
            allSlots.push(...memberData.available_slots)
          }
        })
        slots = [...new Set(allSlots)]
      } else {
      }
    }

    // Filter out past slots for today
    if (shouldFilterByTime) {
      slots = slots.filter((slot) => {
        const [hours, minutes] = slot.split(":").map(Number)
        const slotTime = new Date(now)
        slotTime.setHours(hours, minutes, 0, 0)
        return slotTime >= now
      })
    }

    const sortedSlots = [...slots].sort()

    let nonOverlappingSlots = sortedSlots
    if (selectedTeamMember && currentService) {
      const currentSvcId = currentService?.serviceId || selectedService?.service_id
      if (currentSvcId) {
        nonOverlappingSlots = sortedSlots.filter((slot) => !isOverlapWithExistingSelection(date, slot, selectedTeamMember, currentSvcId))
      }
    }

    const morning = nonOverlappingSlots.filter((slot) => {
      const [hours] = slot.split(":").map(Number)
      return hours < 14
    })

    const afternoon = nonOverlappingSlots.filter((slot) => {
      const [hours] = slot.split(":").map(Number)
      return hours >= 14
    })

    return { morning, afternoon }
  }

  // Get day name in French
  const getDayName = (date) => {
    return format(date, "EEEE", { locale: fr })
  }

  // Get formatted date for display
  const getFormattedDate = (date) => {
    return format(date, "d MMM", { locale: fr })
  }

  // Get team member name by ID
  const getTeamMemberName = (id) => {
    if (!teamMembers) return ""
    const member = teamMembers.find((m) => m.team_member_id === Number.parseInt(id))
    return member ? `${member.user_details?.first_name} ${member.user_details?.last_name}` : ""
  }

  const getServiceDuration = (svcId) => {
    if (!svcId) return 60
    const svc = Array.isArray(allServices) ? allServices.find(x => (x.service_id || x.id) === parseInt(svcId)) : null
    if (svc && svc.duration) return svc.duration
    if (selectedService && (svcId === selectedService?.service_id || svcId === selectedService?.id) && selectedService?.duration) return selectedService.duration
    return 60
  }

  const isOverlapWithExistingSelection = (dateStr, timeStr, teamMemberId, currentServiceId) => {
    try {
      const start = new Date(`${dateStr}T${timeStr}:00`)
      const currentDuration = getServiceDuration(currentServiceId)
      const end = new Date(start.getTime() + currentDuration * 60000)
      for (let i = 0; i < selectedServices.length; i++) {
        if (i === currentServiceIndex) continue
        const s = selectedServices[i]
        if (!s || !s.selectedDate || !s.selectedTime || !s.teamMember) continue
        if (String(s.teamMember) !== String(teamMemberId) || s.selectedDate !== dateStr) continue
        const otherStart = new Date(`${s.selectedDate}T${s.selectedTime}:00`)
        const otherEnd = s.selectedEndTime
          ? new Date(`${s.selectedDate}T${s.selectedEndTime}:00`)
          : new Date(otherStart.getTime() + getServiceDuration(s.serviceId) * 60000)
        if (start < otherEnd && end > otherStart) return true
      }
    } catch (_) {}
    return false
  }

  // Check if date is today
  const isToday = (date) => {
    return isSameDay(date, new Date())
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en'}>
      <Box
        style={{
          backgroundColor: theme.salon.background,
          fontFamily: theme.typography.fontFamily.sans.join(', '),
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          px: { xs: 1, md: 2 },
          py: 2,
        }}
      >
        <Card
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: 3,
            border: "none",
            overflow: "hidden",
            mb: 4,
            width: "100%",
            maxWidth: "1400px",
            backgroundColor: theme.colors.background.secondary,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            {/* {salonDetails && (
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
                  borderRadius: 2,
                  color: "white",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 1.5,
                  }}
                >
                  {salonDetails.name}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                  {salonDetails.address && (
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      📍 {salonDetails.address}
                    </Typography>
                  )}
                  {salonDetails.phone && (
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      📞 {salonDetails.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            )} */}

            {/* Rebooking Banner */}
            {/* {rebookAppointmentData && (
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  background: `linear-gradient(135deg, ${theme.colors.secondary[100]} 0%, ${theme.colors.secondary[200]} 100%)`,
                  borderRadius: 2,
                  border: `1px solid ${theme.colors.secondary[300]}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.colors.secondary[800],
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {isUpdateMode ? '✏️ Modifier un rendez-vous' : '🔄 Reprendre un rendez-vous'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.colors.secondary[700],
                    mb: 1,
                  }}
                >
                  {isUpdateMode 
                    ? `Vous modifiez votre rendez-vous du ${new Date(rebookAppointmentData.start_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                    : `Sélectionnez une nouvelle date et heure pour reprendre votre rendez-vous`
                  }
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.colors.secondary[600],
                    fontSize: '0.875rem',
                  }}
                >
                  {isUpdateMode 
                    ? 'Vous pouvez modifier le service, l\'équipe et choisir une nouvelle date et heure.'
                    : 'Sélectionnez une nouvelle date et heure pour votre rendez-vous.'
                  }
                </Typography>
              </Box>
            )} */}


            {/* Service Selection Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.colors.text.primary,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Choix de la date & heure
              </Typography>
              {!isUpdateMode && onAddService && !!selectedServices[currentServiceIndex]?.selectedSlot && (
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddService}
                  sx={{
                    borderColor: theme.colors.primary[500],
                    color: theme.colors.primary[500],
                    borderWidth: 2,
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    "&:hover": {
                      backgroundColor: theme.colors.primary[50],
                      borderColor: theme.colors.primary[600],
                      borderWidth: 2,
                    },
                    "&:disabled": {
                      borderColor: theme.colors.border.light,
                      color: theme.colors.text.muted,
                    },
                  }}
                >
                  Ajouter un service à la suite
                </Button>
              )}
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: isConsecutive ? "none" : "flex" },
                justifyContent: "center",
                mb: 4,
                gap: { xs: 1, sm: 2 },
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
              }}
            >
              <Button
                variant={timePeriod === "all" ? "contained" : "outlined"}
                onClick={() => setTimePeriod("all")}
                sx={{
                  px: { xs: 2, sm: 4 },
                  py: { xs: 1, sm: 1.5 },
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  textTransform: "none",
                  borderWidth: 2,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "auto", sm: "140px" },
                  background: timePeriod === "all" ? `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)` : "white",
                  color: timePeriod === "all" ? "white" : theme.colors.primary[500],
                  borderColor: theme.colors.primary[500],
                  boxShadow: timePeriod === "all" ? `0 4px 12px ${theme.colors.primary[500]}33` : "none",
                  "&:hover": {
                    background: timePeriod === "all" ? `linear-gradient(135deg, ${theme.salon.primary} 0%, ${theme.colors.primary[800]} 100%)` : theme.colors.background.secondary,
                    borderColor: theme.colors.primary[500],
                    borderWidth: 2,
                  },
                }}
              >
                Toute la journée
              </Button>
            {!isConsecutive && (
              <>
                <Button
                  variant={timePeriod === "morning" ? "contained" : "outlined"}
                  onClick={() => setTimePeriod("morning")}
                  startIcon={
                    !isMobile ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${theme.colors.secondary[600]} 0%, ${theme.colors.secondary[700]} 100%)`,
                        }}
                      />
                    ) : null
                  }
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                    textTransform: "none",
                    borderWidth: 2,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "auto", sm: "120px" },
                    background:
                      timePeriod === "morning" ? `linear-gradient(135deg, ${theme.colors.secondary[600]} 0%, ${theme.colors.secondary[700]} 100%)` : "white",
                    color: timePeriod === "morning" ? "white" : theme.colors.secondary[600],
                    borderColor: theme.colors.secondary[600],
                    boxShadow: timePeriod === "morning" ? `0 4px 12px ${theme.colors.secondary[600]}33` : "none",
                    "&:hover": {
                      background:
                        timePeriod === "morning" ? `linear-gradient(135deg, ${theme.colors.secondary[700]} 0%, ${theme.colors.secondary[800]} 100%)` : theme.colors.secondary[50],
                      borderColor: theme.colors.secondary[600],
                      borderWidth: 2,
                    },
                  }}
                >
                  🌅 Matin
                </Button>
                <Button
                  variant={timePeriod === "afternoon" ? "contained" : "outlined"}
                  onClick={() => setTimePeriod("afternoon")}
                  startIcon={
                    !isMobile ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${theme.colors.secondary[400]} 0%, ${theme.colors.secondary[500]} 100%)`,
                        }}
                      />
                    ) : null
                  }
                  sx={{
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                    textTransform: "none",
                    borderWidth: 2,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "auto", sm: "140px" },
                    background:
                      timePeriod === "afternoon" ? `linear-gradient(135deg, ${theme.colors.secondary[400]} 0%, ${theme.colors.secondary[500]} 100%)` : "white",
                    color: timePeriod === "afternoon" ? "white" : theme.colors.secondary[400],
                    borderColor: timePeriod === "afternoon" ? theme.colors.secondary[400] : theme.colors.secondary[400],
                    boxShadow: timePeriod === "afternoon" ? `0 4px 12px ${theme.colors.secondary[400]}33` : "none",
                    "&:hover": {
                      background:
                        timePeriod === "afternoon" ? `linear-gradient(135deg, ${theme.colors.secondary[500]} 0%, ${theme.colors.secondary[600]} 100%)` : theme.colors.secondary[50],
                      borderColor: theme.colors.secondary[400],
                      borderWidth: 2,
                    },
                  }}
                >
                  🌇 Après-midi
                </Button>
              </>
            )}
            </Box>

            {/* Display API errors */}
            {teamMembersError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {teamMembersError?.data?.debug_info?.errors ? (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Erreurs (Membres):
                    </Typography>
                    <ul style={{ marginTop: "4px", paddingLeft: "20px" }}>
                      {Array.isArray(teamMembersError.data.debug_info.errors) ? (
                        teamMembersError.data.debug_info.errors.map((err, idx) => <li key={idx}>{err}</li>)
                      ) : (
                        <li>{teamMembersError.data.debug_info.errors}</li>
                      )}
                    </ul>
                  </>
                ) : (
                  teamMembersError?.data?.error || teamMembersError?.error || "Erreur lors du chargement des membres"
                )}
              </Alert>
            )}

            {selectedServices.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.colors.text.primary,
                      fontSize: "1.25rem",
                    }}
                  >
                    {/* {isUpdateMode
                      ? 'Modifier le rendez-vous'
                      : rebookAppointmentData
                        ? `Rendez-vous sélectionné${selectedServices.length > 1 ? ` (${selectedServices.length})` : ''}`
                        : `Services sélectionnés (${selectedServices.length})`} */}
                  </Typography>
                  
                  {/* Consecutive booking toggle - for multi-service bookings (2 services -> 2-service API, >2 -> multi-service API) */}
                  {!isUpdateMode && !rebookAppointmentData &&
                    selectedServices.length >= 2 && (
                      <Button
                        variant={selectedServices.length === 2 && isConsecutive ? "contained" : "outlined"}
                        onClick={handleConsecutiveButtonClick}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 600,
                          textTransform: "none",
                          px: 3,
                          py: 1,
                          background: isConsecutive 
                            ? `linear-gradient(135deg, ${theme.colors.secondary[500]} 0%, ${theme.colors.secondary[700]} 100%)`
                            : "white",
                          color: isConsecutive ? "white" : theme.colors.secondary[500],
                          borderColor: theme.colors.secondary[500],
                          borderWidth: 2,
                          boxShadow: isConsecutive ? `0 4px 12px ${theme.colors.secondary[500]}33` : "none",
                          "&:hover": {
                            background: isConsecutive
                              ? `linear-gradient(135deg, ${theme.colors.secondary[600]} 0%, ${theme.colors.secondary[800]} 100%)`
                              : theme.colors.secondary[50],
                            borderColor: theme.colors.secondary[500],
                            borderWidth: 2,
                          },
                        }}
                      >
                        🔗 Consécutif?
                      </Button>
                    )}
                </Box>
                {isUpdateMode ||rebookAppointmentData ?"":<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {selectedServices.map((service, index) => (
                    <Card
                      key={index}
                      sx={{
                        border: `2px solid ${index === currentServiceIndex ? theme.colors.primary[500] : theme.colors.border.light}`,
                        backgroundColor: index === currentServiceIndex ? theme.colors.primary[50] : "white",
                        borderRadius: 2,
                        boxShadow: index === currentServiceIndex ? `0 4px 12px ${theme.colors.primary[500]}26` : "none",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 0 } }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                              {service.serviceName}
                            </Typography>
                            {enableServiceSelection && (
                              <IconButton
                                aria-label={t('client_services.edit_service')}
                                size="small"
                                onClick={() => {
                                  // Navigate to ClientServices for service selection
                                  window.location.href = `/salons/${salonId}?rebookAppointment=${rebookAppointmentData?.appointment_id}`;
                                }}
                                sx={{
                                  color: theme.colors.primary[500],
                                  '&:hover': {
                                    backgroundColor: theme.colors.primary[50],
                                  }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          {service.selectedSlot ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ color: theme.colors.success[500], fontWeight: 500, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                                ✓ {rebookAppointmentData ? 
                                  new Date(rebookAppointmentData.start_time).toLocaleDateString('fr-FR', { 
                                    day: 'numeric', 
                                    month: isMobile ? 'short' : 'long', 
                                    year: 'numeric' 
                                  }) + ' à ' + 
                                  new Date(rebookAppointmentData.start_time).toLocaleTimeString('fr-FR', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })
                                  : `${new Date(service.selectedDate).toLocaleDateString('fr-FR', { day: 'numeric', month: isMobile ? 'short' : 'long' })} à ${service.selectedTime}`}
                                {service.teamMember && !isMobile && ` - ${getTeamMemberName(service.teamMember)}`}
                              </Typography>
                              {service.teamMember && isMobile && (
                                <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontSize: "0.75rem" }}>
                                  {getTeamMemberName(service.teamMember)}
                                </Typography>
                              )}
                              {rebookAppointmentData && (
                                <IconButton
                                  aria-label={t('client_services.edit_time')}
                                  size="small"
                                  onClick={() => {
                                    // Allow editing the time by clearing the selection
                                    const updatedServices = [...selectedServices];
                                    updatedServices[index] = {
                                      ...service,
                                      selectedSlot: null,
                                      selectedDate: null,
                                      selectedTime: null
                                    };
                                    setSelectedServices(updatedServices);
                                  }}
                                  sx={{
                                    color: theme.colors.primary[500],
                                    '&:hover': {
                                      backgroundColor: theme.colors.primary[50],
                                    }
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: theme.colors.warning[500], fontWeight: 500, fontSize: { xs: "0.875rem", sm: "0.875rem" } }}>
                              Sélectionnez un créneau
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: { xs: 1, sm: 1 }, flexDirection: { xs: "row", sm: "row" }, width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "flex-end", sm: "flex-start" } }}>
                          {index !== currentServiceIndex && (
                            <Button
                              size={isMobile ? "medium" : "small"}
                              onClick={() => setCurrentServiceIndex(index)}
                              sx={{
                                color: theme.colors.primary[500],
                                fontWeight: 600,
                                textTransform: "none",
                                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                px: { xs: 2, sm: 1.5 },
                                py: { xs: 0.75, sm: 0.5 },
                                "&:hover": { backgroundColor: theme.colors.gray[50] },
                              }}
                            >
                              Modifier
                            </Button>
                          )}
                          {selectedServices.length > 1 && (
                            <Button
                              size={isMobile ? "medium" : "small"}
                              color="error"
                              onClick={() => {
                                const newServices = selectedServices.filter((_, i) => i !== index)
                                setSelectedServices(newServices)
                                if (currentServiceIndex >= newServices.length) {
                                  setCurrentServiceIndex(Math.max(0, newServices.length - 1))
                                }
                              }}
                              sx={{
                                color: theme.colors.error[500],
                                fontWeight: 600,
                                textTransform: "none",
                                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                px: { xs: 2, sm: 1.5 },
                                py: { xs: 0.75, sm: 0.5 },
                                "&:hover": {
                                  backgroundColor: theme.colors.error[50],
                                },
                              }}
                            >
                              Supprimer
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box> }
                

                {selectedServices.every((service) => service.selectedSlot) && selectedServices.length > 0 && (
                  <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={isUpdateMode ? handleUpdateAppointment : handleConfirmAllAppointments}
                      disabled={isCreating}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
                        color: "#fff",
                        fontWeight: 700,
                        py: 1.75,
                        px: 5,
                        borderRadius: 2,
                        fontSize: "1.05rem",
                        textTransform: "none",
                        boxShadow: `0 4px 14px ${theme.colors.primary[500]}66`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[800]} 100%)`,
                          boxShadow: `0 6px 20px ${theme.colors.primary[500]}80`,
                        },
                      }}
                    >
                      {isCreating ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                      {isUpdateMode
                        ? 'Modifier le rendez-vous'
                        : rebookAppointmentData
                          ? 'Reprogrammer le rendez-vous'
                          : `Confirmer tous les rendez-vous (${selectedServices.length})`}
                    </Button>
                  </Box>
                )}

                {/* Consecutive Slots Section for exactly 2 services */}
                {isConsecutive && selectedServices.length === 2 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        fontWeight: 600,
                        color: theme.colors.secondary[700],
                        fontSize: "1.25rem",
                      }}
                    >
                      🔗 {t('client_services.available_consecutive_slots')}
                    </Typography>

                    {/* Date picker for consecutive booking */}
                    <Box sx={{ mb: 3 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en'}>
                        <DatePicker
                          label="Sélectionner une date"
                          value={consecutiveDate ? dayjs(consecutiveDate) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              setConsecutiveDate(format(newValue.toDate(), "yyyy-MM-dd"))
                            }
                          }}
                          minDate={dayjs()}
                          maxDate={dayjs().add(30, "day")}
                          sx={{ minWidth: 250 }}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>

                    {/* Loading state */}
                    {consecutiveSlotsLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    )}

                    {/* Error state */}
                    {consecutiveSlotsError && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {getApiErrorMessage(consecutiveSlotsError, "Erreur lors du chargement des créneaux consécutifs")}
                      </Alert>
                    )}

                    {/* Consecutive slots display */}
                    {consecutiveSlotsData && (
                      <>
                        {visibleConsecutiveSlots.length === 0 ? (
                          <Box>
                            <Alert severity="info" sx={{ mb: 3 }}>
                              Aucun créneau consécutif disponible pour cette date. 
                              Essayez une autre date ou désactivez le mode consécutif.
                            </Alert>
                            {false && consecutiveSlotsData.debug_info && (
                              <Box sx={{ mt: 2, p: 2, bgcolor: theme.colors.grey[50], borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Informations de débogage :
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Service 1: {consecutiveSlotsData.debug_info.service1_info?.name} - {consecutiveSlotsData.debug_info.service1_info?.available_slots_count} créneaux disponibles
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Service 2: {consecutiveSlotsData.debug_info.service2_info?.name} - {consecutiveSlotsData.debug_info.service2_info?.available_slots_count} créneaux disponibles
                                </Typography>
                                {consecutiveSlotsData.debug_info.warnings?.length > 0 && (
                                  <Typography variant="body2" color="warning.main">
                                    Avertissements: {consecutiveSlotsData.debug_info.warnings.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                            {visibleConsecutiveSlots.map((slot, index) => (
                              <Card
                                key={index}
                                sx={{
                                  cursor: 'pointer',
                                  border: selectedServices[0]?.selectedTime === slot.service1_start 
                                    ? `2px solid ${theme.colors.secondary[500]}` 
                                    : '1px solid #e0e0e0',
                                  backgroundColor: selectedServices[0]?.selectedTime === slot.service1_start 
                                    ? theme.colors.secondary[50] 
                                    : 'white',
                                  '&:hover': {
                                    borderColor: theme.colors.secondary[300],
                                    backgroundColor: theme.colors.secondary[50],
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                                onClick={() => handleConsecutiveSlotSelect(slot)}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <AccessTime fontSize="small" color="secondary" />
                                    <Typography variant="h6" fontWeight={600} color="secondary.main">
                                      {slot.start_time} - {slot.end_time}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ mb: 1 }}>
                                    <Chip
                                      label={`${selectedServices[0]?.serviceName} (${slot.service1_start} - ${slot.service1_end})`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ mb: 1, fontSize: '0.75rem' }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {getTeamMemberName(selectedServices[0]?.teamMember)}
                                    </Typography>
                                  </Box>
                                  
                                  <Box>
                                    <Chip
                                      label={`${selectedServices[1]?.serviceName} (${slot.service2_start} - ${slot.service2_end})`}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{ mb: 1, fontSize: '0.75rem' }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {getTeamMemberName(selectedServices[1]?.teamMember)}
                                    </Typography>
                                  </Box>
                                  
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                                    {t('client_services.total_duration')}: {slot.total_duration} {t('client_services.min')}
                                  </Typography>
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}

                {isConsecutive && selectedServices.length > 2 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        fontWeight: 600,
                        color: theme.colors.secondary[700],
                        fontSize: "1.25rem",
                      }}
                    >
                      🔗 {t('client_services.available_multi_consecutive_slots')}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en'}>
                        <DatePicker
                          label="Sélectionner une date"
                          value={multiConsecutiveDate ? dayjs(multiConsecutiveDate) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const dateStr = format(newValue.toDate(), "yyyy-MM-dd")
                              // Met à jour immédiatement la date sélectionnée pour le libellé et le DatePicker
                              setMultiConsecutiveDate(dateStr)
                              // Recharge les créneaux multi-services pour cette date
                              fetchMultiConsecutiveSlotsForDate(dateStr)
                            }
                          }}
                          minDate={dayjs()}
                          maxDate={dayjs().add(30, "day")}
                          sx={{ minWidth: 250 }}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>

                    {multiConsecutiveDate && (
                      <Typography variant="body2" sx={{ mb: 2, color: theme.colors.text.secondary }}>
                        Date: {multiConsecutiveDate}
                      </Typography>
                    )}

                    {isMultiConsecutiveLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : visibleMultiConsecutiveChains.length === 0 ? (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        Aucun créneau consécutif multi-services encore disponible pour cette date.
                      </Alert>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
                        {visibleMultiConsecutiveChains.map((chain, index) => (
                          <Card
                            key={index}
                            sx={{
                              cursor: 'pointer',
                              border: '1px solid #e0e0e0',
                              '&:hover': {
                                borderColor: theme.colors.secondary[300],
                                backgroundColor: theme.colors.secondary[50],
                              },
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => handleMultiConsecutiveChainSelect(chain)}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <AccessTime fontSize="small" color="secondary" />
                                <Typography variant="h6" fontWeight={600} color="secondary.main">
                                  {chain.start_time} - {chain.end_time}
                                </Typography>
                              </Box>

                              {chain.services && chain.services.map((svcChain, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Chip
                                    label={`${selectedServices[idx]?.serviceName || `Service ${idx + 1}`} (${svcChain.service_start} - ${svcChain.service_end})`}
                                    size="small"
                                    color={idx === 0 ? 'primary' : 'secondary'}
                                    variant="outlined"
                                    sx={{ mb: 0.5, fontSize: '0.75rem' }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {getTeamMemberName(svcChain.team_member_id)}
                                  </Typography>
                                </Box>
                              ))}

                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                                {t('client_services.total_duration')}: {chain.total_duration} {t('client_services.min')}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                <Divider sx={{ mt: 4, mb: 3 }} />
              </Box>
            )}

            {/* Regular booking section - only show when not in consecutive mode */}
            {!isConsecutive && (
              <Box sx={{ mb: 4 }}>
                <FormControl sx={{ minWidth: { xs: "100%", sm: 280 }, width: { xs: "100%", sm: "auto" } }}>
                  <InputLabel id="team-member-select-label" sx={{ fontWeight: 600 }}>
                    Professionnel
                  </InputLabel>
                  <Select
                    labelId="team-member-select-label"
                    id="team-member-select"
                    value={selectedTeamMember}
                    onChange={handleTeamMemberChange}
                    label="Professionnel"
                    disabled={teamMembersLoading}
                    sx={{
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 2,
                        borderColor: theme.colors.border.light,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.colors.border.light,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.colors.primary[500],
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Sans préférence</em>
                    </MenuItem>
                    {filteredTeamMembers.map((member) => (
                      <MenuItem key={member.team_member_id} value={member.team_member_id}>
                        {`${member.user_details?.first_name} ${member.user_details?.last_name}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
                p: { xs: 1.5, sm: 2 },
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", width: { xs: "100%", sm: "auto" }, justifyContent: { xs: "space-between", sm: "center" } }}>
                <IconButton
                  aria-label={t('client_services.previous_week')}
                  onClick={goToPreviousWeek}
                  disabled={!canGoToPreviousWeek()}
                  sx={{
                    mr: { xs: 0, sm: 2 },
                    color: theme.colors.primary[500],
                    backgroundColor: theme.colors.primary[50],
                    "&:hover": {
                      backgroundColor: theme.colors.primary[100],
                      color: theme.colors.primary[600],
                    },
                    "&:disabled": {
                      color: theme.colors.border.light,
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    mx: { xs: 2, sm: 3 },
                    minWidth: { xs: "auto", sm: 250 },
                    textAlign: "center",
                    fontWeight: 700,
                    color: theme.colors.text.primary,
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                    flex: { xs: 1, sm: "none" },
                  }}
                >
                  {isMobile ? (
                    <>
                      {format(weekStart, "d MMM", { locale: fr })} - {format(addDays(weekStart, 6), "d MMM yyyy", { locale: fr })}
                    </>
                  ) : (
                    <>
                      {format(weekStart, "d MMMM", { locale: fr })} - {format(addDays(weekStart, 6), "d MMMM yyyy", { locale: fr })}
                    </>
                  )}
                </Typography>
                <IconButton
                  aria-label={t('client_services.next_week')}
                  onClick={goToNextWeek}
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    color: theme.colors.primary[500],
                    backgroundColor: theme.colors.primary[50],
                    "&:hover": {
                      backgroundColor: theme.colors.primary[100],
                      color: theme.colors.primary[600],
                    },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mt: 4 }}>
              {serviceId || selectedService ? (
                slotsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
                    <CircularProgress size={48} sx={{ color: theme.colors.primary[500] }} />
                  </Box>
                ) : slotsError ? (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {slotsError?.data?.debug_info?.errors ? (
                      <>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Erreurs:
                        </Typography>
                        <ul style={{ marginTop: "4px", paddingLeft: "20px" }}>
                          {Array.isArray(slotsError.data.debug_info.errors) ? (
                            slotsError.data.debug_info.errors.map((err, idx) => <li key={idx}>{err}</li>)
                          ) : (
                            <li>{slotsError.data.debug_info.errors}</li>
                          )}
                        </ul>
                      </>
                    ) : (
                      slotsError?.data?.error ||
                      slotsError?.error ||
                      "Erreur lors du chargement des créneaux disponibles. Veuillez réessayer."
                    )}
                  </Alert>
                ) : isMobile ? (
                  // Mobile: Simple list view
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: 3,
                      overflow: "hidden",
                      border: `2px solid ${theme.colors.border.light}`,
                      p: 2,
                    }}
                  >
                    {/* Mobile date navigation */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                      }}
                    >
                      <IconButton
                        aria-label={t('client_services.previous_day')}
                        onClick={handlePreviousDay}
                        disabled={isPreviousDayDisabled}
                        size="small"
                        sx={{
                          color: theme.colors.primary[500],
                          "&:disabled": {
                            color: theme.colors.border.light,
                          },
                        }}
                      >
                        <ChevronLeft />
                      </IconButton>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: theme.colors.text.primary,
                          textAlign: "center",
                          flex: 1,
                        }}
                      >
                        {format(mobileCurrentDate, "EEEE d MMMM", { locale: fr })}
                      </Typography>

                      <IconButton
                        aria-label={t('client_services.next_day')}
                        onClick={handleNextDay}
                        size="small"
                        sx={{
                          color: theme.colors.primary[500],
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    </Box>

                    {/* All slots for the day */}
                    {(() => {
                      const currentDateStr = format(mobileCurrentDate, "yyyy-MM-dd")
                      const { morning, afternoon } = getAvailableSlotsForDate(currentDateStr)
                      const allSlots = [...morning, ...afternoon]
                      
                      return allSlots.length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {allSlots.map((slot) => {
                            const isSelected = selectedSlot === `${currentDateStr}-${slot}`
                            return (
                              <Button
                                key={slot}
                                variant={isSelected ? "contained" : "outlined"}
                                onClick={() => handleSlotClick(slot, currentDateStr)}
                                sx={{
                                  flex: "1 1 calc(50% - 4px)",
                                  minWidth: 80,
                                  fontSize: "0.875rem",
                                  fontWeight: 700,
                                  borderWidth: 2,
                                  borderColor: isSelected ? theme.colors.primary[500] : theme.colors.border.light,
                                  color: isSelected ? "white" : theme.colors.text.primary,
                                  backgroundColor: isSelected ? theme.colors.primary[500] : "white",
                                  borderRadius: 1.5,
                                  transition: "all 0.2s ease",
                                  textTransform: "none",
                                  "&:hover": {
                                    backgroundColor: isSelected ? theme.colors.primary[600] : theme.colors.primary[50],
                                    borderColor: theme.colors.primary[500],
                                    borderWidth: 2,
                                  },
                                }}
                              >
                                {slot}
                              </Button>
                            )
                          })}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.colors.text.secondary,
                            textAlign: "center",
                            py: 3,
                          }}
                        >
                          Aucun créneau disponible
                        </Typography>
                      )
                    })()}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      backgroundColor: "white",
                      borderRadius: 3,
                      overflow: "hidden",
                      border: `2px solid ${theme.colors.border.light}`,
                    }}
                  >
                    {/* Day Headers */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "60px 1fr",
                          sm: "80px repeat(3, 1fr)",
                          md: "100px repeat(4, 1fr)",
                          lg: `120px repeat(${weekDays.length}, 1fr)`,
                        },
                        gap: 0,
                        borderBottom: `3px solid ${theme.colors.primary[500]}`,
                        background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%)`,
                      }}
                    >
                      {/* Empty corner cell */}
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRight: "2px solid rgba(255,255,255,0.2)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "white",
                            fontSize: { xs: "0.75rem", md: "0.85rem" },
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Heure
                        </Typography>
                      </Box>

                      {/* Day headers */}
                      {isMobile ? (
                        // Mobile: Show current day with navigation arrows
                        <Box
                          sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.15)",
                            width: "100%",
                          }}
                        >
                          <IconButton
                            aria-label={t('client_services.previous_day')}
                            onClick={handlePreviousDay}
                            disabled={isPreviousDayDisabled}
                            sx={{
                              color: "white",
                              opacity: isPreviousDayDisabled ? 0.3 : 1,
                              "&:disabled": {
                                color: "rgba(255,255,255,0.3)",
                              },
                            }}
                          >
                            <ChevronLeft />
                          </IconButton>
                          
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 700,
                                color: "white",
                                textTransform: "capitalize",
                                mb: 0.5,
                                fontSize: "0.85rem",
                              }}
                            >
                              {isToday(mobileCurrentDate) ? "Aujourd'hui" : format(mobileCurrentDate, "EEEE", { locale: fr })}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "rgba(255,255,255,0.9)",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                              }}
                            >
                              {format(mobileCurrentDate, "d MMM", { locale: fr })}
                            </Typography>
                          </Box>

                          <IconButton
                            aria-label={t('client_services.next_day')}
                            onClick={handleNextDay}
                            sx={{
                              color: "white",
                              // ml: -3,
                            }}
                          >
                            <ChevronRight />
                          </IconButton>
                        </Box>
                      ) : (
                        // Desktop: Show multiple days
                        weekDays.map((date, index) => {
                          const dateStr = format(date, "yyyy-MM-dd")
                          const dayName = getDayName(date)
                          const formattedDate = getFormattedDate(date)
                          const todayClass = isToday(date)

                          return (
                            <Box
                              key={dateStr}
                              sx={{
                                p: 2,
                                display: {
                                  xs: index >= 2 ? "none" : "flex",
                                  sm: index >= 3 ? "none" : "flex",
                                  md: index >= 4 ? "none" : "flex",
                                  lg: "flex",
                                },
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRight: index < weekDays.length - 1 ? "2px solid rgba(255,255,255,0.2)" : "none",
                                backgroundColor: todayClass ? "rgba(255,255,255,0.15)" : "transparent",
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 700,
                                  color: "white",
                                  textTransform: "capitalize",
                                  mb: 0.5,
                                  fontSize: { xs: "0.85rem", md: "1rem" },
                                }}
                              >
                                {dayName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "rgba(255,255,255,0.9)",
                                  fontWeight: 600,
                                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                                }}
                              >
                                {formattedDate}
                              </Typography>
                              {todayClass && (
                                <Chip
                                  label="Aujourd'hui"
                                  size="small"
                                  sx={{
                                    mt: 0.5,
                                    backgroundColor: "white",
                                    color: theme.colors.primary[500],
                                    fontWeight: 700,
                                    fontSize: "0.65rem",
                                    height: 20,
                                  }}
                                />
                              )}
                            </Box>
                          )
                        })
                      )}
                    </Box>

                    {/* Time slots grid */}
                    <Box>
                      {(() => {
                        // Collect all unique time slots across all days
                        const allTimeSlots = new Set()
                        weekDays.forEach((date) => {
                          const dateStr = format(date, "yyyy-MM-dd")
                          const { morning, afternoon } = getAvailableSlotsForDate(dateStr)
                          morning.forEach((slot) => allTimeSlots.add(slot))
                          afternoon.forEach((slot) => allTimeSlots.add(slot))
                        })

                        // Sort time slots
                        const sortedTimeSlots = Array.from(allTimeSlots).sort()

                        // Separate into morning and afternoon
                        const morningSlots = sortedTimeSlots.filter((slot) => {
                          const [hours] = slot.split(":").map(Number)
                          return hours < 14
                        })

                        const afternoonSlots = sortedTimeSlots.filter((slot) => {
                          const [hours] = slot.split(":").map(Number)
                          return hours >= 14
                        })

                        const displayMorning = timePeriod === "all" || timePeriod === "morning"
                        const displayAfternoon = timePeriod === "all" || timePeriod === "afternoon"

                        return (
                          <>
                            {/* Morning Section */}
                            {displayMorning && morningSlots.length > 0 && (
                              <Box>
                                {/* Morning header */}
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "60px 1fr",
                                      sm: "80px repeat(3, 1fr)",
                                      md: "100px repeat(4, 1fr)",
                                      lg: `120px repeat(${weekDays.length}, 1fr)`,
                                    },
                                    backgroundColor: theme.colors.secondary[100],
                                    borderBottom: `2px solid ${theme.colors.secondary[600]}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      borderRight: `2px solid ${theme.colors.secondary[600]}`,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: `linear-gradient(135deg, ${theme.colors.secondary[600]} 0%, ${theme.colors.secondary[700]} 100%)`,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 700,
                                        color: theme.colors.secondary[900],
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                                      }}
                                    >
                                      Matin
                                    </Typography>
                                  </Box>
                                  {!isMobile && weekDays.map((date, index) => (
                                    <Box
                                      key={format(date, "yyyy-MM-dd")}
                                      sx={{
                                        display: {
                                          xs: index >= 2 ? "none" : "block",
                                          sm: index >= 3 ? "none" : "block",
                                          md: index >= 4 ? "none" : "block",
                                          lg: "block",
                                        },
                                        borderRight: index < weekDays.length - 1 ? `1px solid ${theme.colors.secondary[600]}` : "none",
                                      }}
                                    />
                                  ))}
                                </Box>

                                {/* Morning time slots */}
                                {morningSlots.map((slot, slotIndex) => (
                                  <Box
                                    key={slot}
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "60px 1fr",
                                        sm: "80px repeat(3, 1fr)",
                                        md: "100px repeat(4, 1fr)",
                                        lg: `120px repeat(${weekDays.length}, 1fr)`,
                                      },
                                      borderBottom:
                                        slotIndex < morningSlots.length - 1 ? `1px solid ${theme.colors.border.light}` : `2px solid ${theme.colors.border.medium}`,
                                    }}
                                  >
                                    {/* Time label */}
                                    <Box
                                      sx={{
                                        p: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: theme.colors.primary[50],
                                        borderRight: `2px solid ${theme.colors.border.light}`,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          color: theme.colors.text.primary,
                                          fontSize: { xs: "0.85rem", md: "0.95rem" },
                                        }}
                                      >
                                        {slot}
                                      </Typography>
                                    </Box>

                                    {/* Slot buttons for each day */}
                                    {isMobile ? (
                                      // Mobile: Show only current mobile date's slots
                                      (() => {
                                        const currentDateStr = format(mobileCurrentDate, "yyyy-MM-dd")
                                        const { morning } = getAvailableSlotsForDate(currentDateStr)
                                        const isAvailable = morning.includes(slot)
                                        const isSelected = selectedSlot === `${currentDateStr}-${slot}`

                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              p: 0.5,
                                              backgroundColor: isSelected ? theme.colors.primary[50] : theme.colors.background.primary,
                                            }}
                                          >
                                            {isAvailable ? (
                                              <Button
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => handleSlotClick(slot, currentDateStr)}
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  fontSize: "0.75rem",
                                                  fontWeight: 700,
                                                  borderWidth: 2,
                                                  borderColor: isSelected ? theme.salon.primary : theme.colors.border.light,
                                                  color: isSelected ? (theme.salon.compatibilityMode ? "#fff" : theme.salon.onPrimary) : theme.colors.text.primary,
                                                  background: isSelected
                                                    ? `linear-gradient(135deg, ${theme.salon.primary} 0%, ${theme.salon.secondary} 100%)`
                                                    : "white",
                                                  borderRadius: 1.5,
                                                  transition: "all 0.2s ease",
                                                  textTransform: "none",
                                                  boxShadow: isSelected ? `0 4px 12px ${alpha(theme.salon.primary, 0.3)}` : "none",
                                                  "&:hover": {
                                                    background: isSelected
                                                      ? `linear-gradient(135deg, ${theme.colors.primary[700]} 0%, ${theme.colors.secondary[700]} 100%)`
                                                      : theme.colors.background.secondary,
                                                    borderColor: theme.salon.primary,
                                                    borderWidth: 2,
                                                    transform: "scale(1.02)",
                                                    boxShadow: `0 4px 12px ${alpha(theme.salon.primary, 0.2)}`,
                                                  },
                                                }}
                                              >
                                                ✓
                                              </Button>
                                            ) : (
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  backgroundColor: theme.colors.primary[50],
                                                  borderRadius: 1.5,
                                                  border: `2px dashed ${theme.colors.border.medium}`,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: theme.colors.text.muted,
                                                    fontSize: "0.65rem",
                                                  }}
                                                >
                                                  -
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        )
                                      })()
                                    ) : (
                                      // Desktop: Show multiple days
                                      weekDays.map((date, dayIndex) => {
                                        const dateStr = format(date, "yyyy-MM-dd")
                                        const { morning } = getAvailableSlotsForDate(dateStr)
                                        const isAvailable = morning.includes(slot)
                                        const isSelected = selectedSlot === `${dateStr}-${slot}`

                                        return (
                                          <Box
                                            key={dateStr}
                                            sx={{
                                              display: {
                                                xs: dayIndex >= 2 ? "none" : "flex",
                                                sm: dayIndex >= 3 ? "none" : "flex",
                                                md: dayIndex >= 4 ? "none" : "flex",
                                                lg: "flex",
                                              },
                                              alignItems: "center",
                                              justifyContent: "center",
                                              p: 0.5,
                                              borderRight: dayIndex < weekDays.length - 1 ? `1px solid ${theme.colors.border.light}` : "none",
                                              backgroundColor: isSelected ? theme.colors.primary[50] : theme.colors.background.primary,
                                            }}
                                          >
                                            {isAvailable ? (
                                              <Button
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => handleSlotClick(slot, dateStr)}
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                                                  fontWeight: 700,
                                                  borderWidth: 2,
                                                  borderColor: isSelected ? theme.salon.primary : theme.colors.border.light,
                                                  color: isSelected ? (theme.salon.compatibilityMode ? "#fff" : theme.salon.onPrimary) : theme.colors.text.primary,
                                                  background: isSelected
                                                    ? `linear-gradient(135deg, ${theme.salon.primary} 0%, ${theme.salon.secondary} 100%)`
                                                    : "white",
                                                  borderRadius: 1.5,
                                                  transition: "all 0.2s ease",
                                                  textTransform: "none",
                                                  boxShadow: isSelected ? `0 4px 12px ${alpha(theme.salon.primary, 0.3)}` : "none",
                                                  "&:hover": {
                                                    background: isSelected
                                                      ? `linear-gradient(135deg, ${theme.colors.primary[700]} 0%, ${theme.colors.secondary[700]} 100%)`
                                                      : theme.colors.background.secondary,
                                                    borderColor: theme.salon.primary,
                                                    borderWidth: 2,
                                                    transform: "scale(1.02)",
                                                    boxShadow: `0 4px 12px ${alpha(theme.salon.primary, 0.2)}`,
                                                  },
                                                }}
                                              >
                                                ✓
                                              </Button>
                                            ) : (
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  backgroundColor: theme.colors.primary[50],
                                                  borderRadius: 1.5,
                                                  border: `2px dashed ${theme.colors.border.medium}`,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: theme.colors.text.muted,
                                                    fontSize: { xs: "0.65rem", md: "0.75rem" },
                                                  }}
                                                >
                                                  -
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        )
                                      })
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Afternoon Section */}
                            {displayAfternoon && afternoonSlots.length > 0 && (
                              <Box>
                                {/* Afternoon header */}
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "60px 1fr",
                                      sm: "80px repeat(3, 1fr)",
                                      md: "100px repeat(4, 1fr)",
                                      lg: `120px repeat(${weekDays.length}, 1fr)`,
                                    },
                                    backgroundColor: theme.colors.secondary[50],
                                    borderBottom: `2px solid ${theme.colors.secondary[400]}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      borderRight: `2px solid ${theme.colors.secondary[400]}`,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: `linear-gradient(135deg, ${theme.colors.secondary[400]} 0%, ${theme.colors.secondary[500]} 100%)`,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 700,
                                        color: theme.colors.secondary[900],
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                                      }}
                                    >
                                      Après-midi
                                    </Typography>
                                  </Box>
                                  {!isMobile && weekDays.map((date, index) => (
                                    <Box
                                      key={format(date, "yyyy-MM-dd")}
                                      sx={{
                                        display: {
                                          xs: index >= 2 ? "none" : "block",
                                          sm: index >= 3 ? "none" : "block",
                                          md: index >= 4 ? "none" : "block",
                                          lg: "block",
                                        },
                                        borderRight: index < weekDays.length - 1 ? `1px solid ${theme.colors.secondary[400]}` : "none",
                                      }}
                                    />
                                  ))}
                                </Box>

                                {/* Afternoon time slots */}
                                {afternoonSlots.map((slot, slotIndex) => (
                                  <Box
                                    key={slot}
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "60px 1fr",
                                        sm: "80px repeat(3, 1fr)",
                                        md: "100px repeat(4, 1fr)",
                                        lg: `120px repeat(${weekDays.length}, 1fr)`,
                                      },
                                      borderBottom:
                                        slotIndex < afternoonSlots.length - 1 ? `1px solid ${theme.colors.border.light}` : "none",
                                    }}
                                  >
                                    {/* Time label */}
                                    <Box
                                      sx={{
                                        p: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: theme.colors.primary[50],
                                        borderRight: `2px solid ${theme.colors.border.light}`,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          color: theme.colors.text.primary,
                                          fontSize: { xs: "0.85rem", md: "0.95rem" },
                                        }}
                                      >
                                        {slot}
                                      </Typography>
                                    </Box>

                                    {/* Slot buttons for each day */}
                                    {isMobile ? (
                                      // Mobile: Show only current mobile date's slots
                                      (() => {
                                        const currentDateStr = format(mobileCurrentDate, "yyyy-MM-dd")
                                        const { afternoon } = getAvailableSlotsForDate(currentDateStr)
                                        const isAvailable = afternoon.includes(slot)
                                        const isSelected = selectedSlot === `${currentDateStr}-${slot}`

                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              p: 0.5,
                                              backgroundColor: isSelected ? theme.colors.primary[50] : theme.colors.background.primary,
                                            }}
                                          >
                                            {isAvailable ? (
                                              <Button
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => handleSlotClick(slot, currentDateStr)}
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  fontSize: "0.75rem",
                                                  fontWeight: 700,
                                                  borderWidth: 2,
                                                  borderColor: isSelected ? theme.salon.primary : theme.colors.border.light,
                                                  color: isSelected ? (theme.salon.compatibilityMode ? "#fff" : theme.salon.onPrimary) : theme.colors.text.primary,
                                                  background: isSelected
                                                    ? `linear-gradient(135deg, ${theme.salon.primary} 0%, ${theme.salon.secondary} 100%)`
                                                    : "white",
                                                  borderRadius: 1.5,
                                                  transition: "all 0.2s ease",
                                                  textTransform: "none",
                                                  boxShadow: isSelected ? `0 4px 12px ${alpha(theme.salon.primary, 0.3)}` : "none",
                                                  "&:hover": {
                                                    background: isSelected
                                                      ? `linear-gradient(135deg, ${theme.colors.primary[700]} 0%, ${theme.colors.secondary[700]} 100%)`
                                                      : theme.colors.background.secondary,
                                                    borderColor: theme.salon.primary,
                                                    borderWidth: 2,
                                                    transform: "scale(1.02)",
                                                    boxShadow: `0 4px 12px ${alpha(theme.salon.primary, 0.2)}`,
                                                  },
                                                }}
                                              >
                                                ✓
                                              </Button>
                                            ) : (
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  backgroundColor: theme.colors.primary[50],
                                                  borderRadius: 1.5,
                                                  border: `2px dashed ${theme.colors.border.medium}`,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: theme.colors.text.muted,
                                                    fontSize: "0.65rem",
                                                  }}
                                                >
                                                  -
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        )
                                      })()
                                    ) : (
                                      // Desktop: Show multiple days
                                      weekDays.map((date, dayIndex) => {
                                        const dateStr = format(date, "yyyy-MM-dd")
                                        const { afternoon } = getAvailableSlotsForDate(dateStr)
                                        const isAvailable = afternoon.includes(slot)
                                        const isSelected = selectedSlot === `${dateStr}-${slot}`

                                        return (
                                          <Box
                                            key={dateStr}
                                            sx={{
                                              display: {
                                                xs: dayIndex >= 2 ? "none" : "flex",
                                                sm: dayIndex >= 3 ? "none" : "flex",
                                                md: dayIndex >= 4 ? "none" : "flex",
                                                lg: "flex",
                                              },
                                              alignItems: "center",
                                              justifyContent: "center",
                                              p: 0.5,
                                              borderRight: dayIndex < weekDays.length - 1 ? `1px solid ${theme.colors.border.light}` : "none",
                                              backgroundColor: isSelected ? theme.colors.primary[50] : theme.colors.background.primary,
                                            }}
                                          >
                                            {isAvailable ? (
                                                <Button
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => handleSlotClick(slot, dateStr)}
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  fontSize: { xs: "0.75rem", md: "0.85rem" },
                                                  fontWeight: 700,
                                                  borderWidth: 2,
                                                  borderColor: isSelected ? theme.salon.primary : theme.colors.border.light,
                                                  color: isSelected ? (theme.salon.compatibilityMode ? "#fff" : theme.salon.onPrimary) : theme.colors.text.primary,
                                                  background: isSelected
                                                    ? `linear-gradient(135deg, ${theme.salon.primary} 0%, ${theme.salon.secondary} 100%)`
                                                    : "white",
                                                  borderRadius: 1.5,
                                                  transition: "all 0.2s ease",
                                                  textTransform: "none",
                                                  boxShadow: isSelected ? `0 4px 12px ${alpha(theme.salon.primary, 0.3)}` : "none",
                                                  "&:hover": {
                                                    background: isSelected
                                                      ? `linear-gradient(135deg, ${theme.colors.primary[700]} 0%, ${theme.colors.secondary[700]} 100%)`
                                                      : theme.colors.background.secondary,
                                                    borderColor: theme.salon.primary,
                                                    borderWidth: 2,
                                                    transform: "scale(1.02)",
                                                    boxShadow: `0 4px 12px ${alpha(theme.salon.primary, 0.2)}`,
                                                  },
                                                }}
                                              >
                                                ✓
                                              </Button>
                                            ) : (
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                  height: "100%",
                                                  minHeight: 48,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  backgroundColor: theme.colors.primary[50],
                                                  borderRadius: 1.5,
                                                  border: `2px dashed ${theme.colors.border.medium}`,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: theme.colors.text.muted,
                                                    fontSize: { xs: "0.65rem", md: "0.75rem" },
                                                  }}
                                                >
                                                  -
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        )
                                      })
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* No slots available message */}
                            {((displayMorning && morningSlots.length === 0) || !displayMorning) &&
                              ((displayAfternoon && afternoonSlots.length === 0) || !displayAfternoon) && (
                                <Box
                                  sx={{
                                    textAlign: "center",
                                    py: 6,
                                    backgroundColor: theme.colors.primary[50],
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: theme.colors.text.secondary,
                                      fontWeight: 500,
                                    }}
                                  >
                                    Aucun créneau disponible pour cette période
                                  </Typography>
                                </Box>
                              )}
                          </>
                        )
                      })()}
                    </Box>
                  </Box>
                )
              ) : (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  Veuillez sélectionner un service pour voir les créneaux disponibles.
                </Alert>
              )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      
      {/* Success Dialog */}
      <MuiDialog
        open={successDialogOpen}
        onExternalClose={() => {
          setSuccessDialogOpen(false)
          // Navigate back to services after user closes the dialog
          if (onSelectSlot) {
            onSelectSlot({ multipleAppointments: appointmentResults, resetServices: true })
          }
        }}
        icon={<CheckCircle sx={{ color: theme.colors.primary[500] }} />}
        title={t(`appointment_workflow.booking.${bookingOutcome}_title`, {
          defaultValue: t(`appointment_workflow.booking.${bookingOutcome}`),
        })}
        subtitle={t(`appointment_workflow.booking.${bookingOutcome}`)}
        content={
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, fontWeight: 600, color: theme.colors.text.primary }}>
              Détails de vos rendez-vous :
            </Typography>
            {appointmentResults.map((appointment, index) => (
              <Card 
                key={index} 
                sx={{ 
                  mb: 2, 
                  p: 3, 
                  bgcolor: theme.colors.primary[50],
                  border: `1px solid ${theme.colors.primary[200]}`,
                  borderRadius: 2
                }}
              >
                {/* Nom de la prestation */}
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.primary[700], mb: 2 }}>
                  {appointment.serviceName}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                      📅 Date :
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                      {new Date(appointment.start_time).toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </Typography>
                  </Box>

                  {/* Heure de début */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                      🕐 Début :
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                      {new Date(appointment.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>

                  {/* Heure de fin */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                      🕐 Fin :
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                      {new Date(appointment.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>

                  {/* Employé */}
                  {appointment.team_member_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                        👤 Avec :
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                        {appointment.team_member_name}
                      </Typography>
                    </Box>
                  )}

                  {/* Durée */}
                  {appointment.duration && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                        ⏱️ Durée :
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                        {appointment.duration}
                      </Typography>
                    </Box>
                  )}

                  {/* Prix */}
                  {appointment.price && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                        💰 Prix :
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.colors.secondary[600], fontWeight: 700 }}>
                        {appointment.price} DA
                      </Typography>
                    </Box>
                  )}

                  {/* Statut */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.text.primary, minWidth: '100px' }}>
                      📍 Statut :
                    </Typography>
                    <Chip 
                      label={t(appointmentStatusLabelKey(appointment.status))}
                      color={appointment.status === 'confirmed' ? 'success' : 'warning'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                {/* Bouton Voir mes RDV */}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    setSuccessDialogOpen(false);
                    navigate('/client/appointments', { 
                      state: { 
                        highlightAppointmentId: appointment.id || appointment.appointment_id,
                        scrollToAppointment: true 
                      } 
                    });
                  }}
                  sx={{
                    mt: 2,
                    backgroundColor: theme.colors.primary[600],
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.colors.primary[700]
                    }
                  }}
                >
                  📅 Voir mes rendez-vous
                </Button>
              </Card>
            ))}
          </Box>
        }
        maxWidth="md"
      />
      
      {/* Error Dialog */}
      <MuiDialog
        open={errorDialogOpen}
        onExternalClose={() => setErrorDialogOpen(false)}
        icon={<Error sx={{ color: theme.colors.error[500] }} />}
        title="Erreur de création"
        subtitle="Une erreur s'est produite lors de la création de vos rendez-vous"
        content={
          <Box sx={{ mt: 2 }}>
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
              {errorMessage}
            </Alert>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Veuillez réessayer ou contacter le salon directement si le problème persiste.
            </Typography>
          </Box>
        }
        maxWidth="sm"
      />

      {/* Authentication Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        userType="client"
        onAuthSuccess={handleAuthSuccess}
      />

      {/* Appointment Booking Dialog */}
      <AppointmentBookingDialog
        open={bookingDialogOpen}
        onClose={handleBookingDialogClose}
        onSuccess={handleAppointmentSuccess}
        salonId={salonId}
        serviceId={serviceId}
        serviceName={serviceName}
        appointmentDetails={appointmentToConfirm}
      />

      {/* Selection Conflict Dialog */}
      {renderSelectionConflictDialog()}
    </LocalizationProvider>
  )
}

export default AppointmentCalendar
